import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import { Box, Card, Divider, IconButton, Typography } from '@mui/material';
import { DraggableProvided, DraggableStateSnapshot, Droppable } from 'react-beautiful-dnd';
import { AvatarCard, AvatarImage, AvatarInfo, DragHandle, DropZone, GroupContainer } from '../../shared/AvatarsOrdering';
import { AvatarGroup } from '../hooks/useAvatarOrdering';

interface GroupItemProps {
    group: AvatarGroup;
    index: number;
    provided: DraggableProvided;
    snapshot: DraggableStateSnapshot;
    onUngroupAvatar: (groupId: string) => void;
    onRemoveFromGroup: (groupId: string, avatarId: string) => void;
}

export const GroupItem = ({ group, index, provided, snapshot, onUngroupAvatar, onRemoveFromGroup }: GroupItemProps) => {
    return (
        <Box>
            <AvatarCard ref={provided.innerRef} {...provided.draggableProps} isDragging={snapshot.isDragging}>
                <DragHandle {...provided.dragHandleProps}>
                    <DragIndicatorIcon />
                </DragHandle>
                <GroupWorkIcon sx={{ mr: 1, color: 'secondary.main' }} />
                <AvatarImage src={group.mainAvatar.thumbnail || group.mainAvatar.poster} alt={group.mainAvatar.name} />
                <AvatarInfo>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                        #{index + 1} - {group.mainAvatar.name} (Group {group.alternatives.length + 1})
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {group.alternatives.length} alternatives
                    </Typography>
                </AvatarInfo>
                <IconButton size="small" onClick={() => onUngroupAvatar(group.id)} title="Ungroup" color="error">
                    <GroupWorkIcon />
                </IconButton>
            </AvatarCard>

            <GroupContainer>
                <Droppable droppableId={`group-${group.id}`} type="AVATAR">
                    {(provided, snapshot) => (
                        <Box {...provided.droppableProps} ref={provided.innerRef} sx={{ minHeight: '60px' }}>
                            {group.alternatives.length === 0 ? (
                                <DropZone className={snapshot.isDraggingOver ? 'drag-over' : ''}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        Drag avatars here to add alternatives
                                    </Typography>
                                </DropZone>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {group.alternatives.map(alt => (
                                        <Card key={alt.id} sx={{ p: 1, display: 'flex', alignItems: 'center' }}>
                                            <AvatarImage
                                                src={alt.thumbnail || alt.poster}
                                                alt={alt.name}
                                                width="40px"
                                                height="40px"
                                                sx={{ mr: 1 }}
                                            />
                                            <Typography variant="body2" sx={{ flex: 1, fontSize: '0.85rem' }}>
                                                {alt.name}
                                            </Typography>
                                            <IconButton size="small" onClick={() => onRemoveFromGroup(group.id, alt.id)} color="error">
                                                ×
                                            </IconButton>
                                        </Card>
                                    ))}
                                </Box>
                            )}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
            </GroupContainer>

            <Divider sx={{ my: 2 }} />
        </Box>
    );
};
